import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Search, TrendingUp, Users, X, ToggleRight } from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import axios from 'axios';
import { API_URL, getAuthHeaders } from '../../services/api';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import useRangeSelect from '../../hooks/useRangeSelect';
import DragSelectOverlay from '../../components/common/DragSelectOverlay';
import FloatingActionBar from '../../components/common/FloatingActionBar';
import ConfirmModal from '../../components/common/ConfirmModal';

const CustomerDiscountModal = ({ discount, businesses, onClose, onSave, categories: initialCategories }) => {
  const { t } = useTranslation(['wholesaler', 'products']);
  const [formData, setFormData] = useState({
    businessId: discount?.businessId?._id || '',
    percentage: discount?.percentage || '',
    reason: discount?.reason || '',
    minimumOrderAmount: discount?.minimumOrderAmount || 0,
    expiresAt: discount?.expiresAt ? new Date(discount.expiresAt).toISOString().split('T')[0] : '',
    active: discount?.active !== undefined ? discount.active : true,
    targetType: discount?.targetType || 'all',
    targetValue: discount?.targetValue || '',
    targetCategory: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [approvedBusinesses, setApprovedBusinesses] = useState([]);
  const [categories, setCategories] = useState(initialCategories || []);
  const [subcategories, setSubcategories] = useState([]);

  // Fetch categories if not provided
  useEffect(() => {
    if (categories.length === 0) {
      axios.get(`${API_URL}/catalog-discounts/categories`, { headers: getAuthHeaders() })
        .then(res => setCategories(res.data.categories || []))
        .catch(() => {});
    }
  }, []);

  // Fetch subcategories when category selected for subcategory targeting
  useEffect(() => {
    if (formData.targetType === 'subcategory' && formData.targetCategory) {
      axios.get(`${API_URL}/catalog-discounts/subcategories?category=${encodeURIComponent(formData.targetCategory)}`, { headers: getAuthHeaders() })
        .then(res => setSubcategories(res.data.subcategories || []))
        .catch(() => setSubcategories([]));
    } else {
      setSubcategories([]);
    }
  }, [formData.targetType, formData.targetCategory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.businessId) {
      setError(t('customerDiscounts.errors.selectCustomer'));
      return;
    }
    
    if (!formData.percentage || formData.percentage < 0 || formData.percentage > 100) {
      setError(t('customerDiscounts.errors.invalidPercentage'));
      return;
    }

    if (formData.targetType && formData.targetType !== 'all' && !formData.targetValue) {
      setError(t('customerDiscounts.errors.selectTarget', 'Please select a category or subcategory'));
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await onSave({
        businessId: formData.businessId,
        percentage: parseFloat(formData.percentage),
        reason: formData.reason,
        minimumOrderAmount: parseFloat(formData.minimumOrderAmount) || 0,
        expiresAt: formData.expiresAt || null,
        active: formData.active,
        targetType: formData.targetType || 'all',
        targetValue: formData.targetType !== 'all' ? formData.targetValue : null
      });
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      // ✅ FIX: Show the specific error message from the backend (e.g., "Duplicate discount")
      const msg = err.response?.data?.message || err.message || t('customerDiscounts.errors.saveFailed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">
            {discount ? t('customerDiscounts.modal.editTitle') : t('customerDiscounts.modal.addTitle')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('customerDiscounts.modal.customer')} <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.businessId}
              onChange={(e) => setFormData({ ...formData, businessId: e.target.value })}
              disabled={!!discount}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            >
              <option value="">{t('customerDiscounts.modal.selectPlaceholder')}</option>
              {businesses.map(business => (
                <option key={business._id} value={business._id}>
                  {business.businessName}{!business.hasOrdered && " ★ (" + t("customerDiscounts.catalogPartner") + ")"}
                </option>
              ))}
            </select>
          </div>
            {formData.businessId && (() => {
              const selectedBusiness = businesses.find(b => b._id === formData.businessId);
              return selectedBusiness && !selectedBusiness.hasOrdered && (
                <p className="mt-2 text-sm text-blue-600 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  {t("customerDiscounts.catalogPartnerInfo")}
                </p>
              );
            })()}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('customerDiscounts.modal.percentage')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={formData.percentage}
                onChange={(e) => setFormData({ ...formData, percentage: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">%</span>
            </div>
          </div>

          {/* Apply To — targeting selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('customerDiscounts.modal.applyTo', 'Apply to')}
            </label>
            <div className="flex gap-2 mb-2">
              {['all', 'category', 'subcategory'].map(tt => (
                <button key={tt} type="button" onClick={() => setFormData({ ...formData, targetType: tt, targetValue: '', targetCategory: '' })}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${formData.targetType === tt ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  {tt === 'all' ? t('customerDiscounts.modal.allProducts', 'All Products')
                    : tt === 'category' ? t('customerDiscounts.modal.category', 'Category')
                    : t('customerDiscounts.modal.subcategory', 'Subcategory')}
                </button>
              ))}
            </div>
            {formData.targetType === 'category' && (
              <select value={formData.targetValue} onChange={e => setFormData({ ...formData, targetValue: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">{t('catalogDiscounts.modal.selectCategory', 'Select category...')}</option>
                {categories.map(c => <option key={c} value={c}>{t(`products:categories.${c}`, c?.replace(/_/g, ' '))}</option>)}
              </select>
            )}
            {formData.targetType === 'subcategory' && (
              <div className="space-y-2">
                <select value={formData.targetCategory} onChange={e => setFormData({ ...formData, targetCategory: e.target.value, targetValue: '' })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="">{t('catalogDiscounts.modal.selectCategoryFirst', 'Select a category first')}</option>
                  {categories.map(c => <option key={c} value={c}>{t(`products:categories.${c}`, c?.replace(/_/g, ' '))}</option>)}
                </select>
                <select value={formData.targetValue} onChange={e => setFormData({ ...formData, targetValue: e.target.value })}
                  disabled={!formData.targetCategory}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100">
                  <option value="">{t('catalogDiscounts.modal.selectSubcategory', 'Select subcategory...')}</option>
                  {subcategories.map(sc => <option key={sc} value={sc}>{t(`products:subcategories.${sc}`, sc?.replace(/_/g, ' '))}</option>)}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('customerDiscounts.modal.reason')}</label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('customerDiscounts.modal.minOrder')}
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">€</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.minimumOrderAmount}
                onChange={(e) => setFormData({ ...formData, minimumOrderAmount: e.target.value })}
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('customerDiscounts.modal.expiration')}
            </label>
            <input
              type="date"
              value={formData.expiresAt}
              onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {discount && (
            <div>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">{t('customerDiscounts.modal.isActive')}</span>
              </label>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={loading} className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              {loading ? t('common.saving') : discount ? t('customerDiscounts.modal.updateBtn') : t('customerDiscounts.modal.createBtn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CustomerDiscounts = () => {
  const { t } = useTranslation(['wholesaler', 'common', 'products']);
  const [discounts, setDiscounts] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [approvedBusinesses, setApprovedBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    fetchDiscounts();
    fetchAnalytics();
    fetchBusinesses();
    fetchApprovedBusinesses();
  }, [showActiveOnly]);
  
  const fetchDiscounts = async () => {
    try {
      setLoading(true);
      
      // ✅ FIX: Logic for "Show Active Only" checkbox
      // If Checked (true): Send active=true -> Backend returns only active
      // If Unchecked (false): Send NO param -> Backend returns ALL (Active + Inactive)
      const config = {
        headers: getAuthHeaders(),
        params: showActiveOnly ? { active: 'true' } : {} 
      };

      const response = await axios.get(`${API_URL}/discounts/customers`, config);
      
      setDiscounts(response.data.discounts || []);
      setUpgradeRequired(false);
    } catch (err) {
      console.error('Fetch discounts error:', err);
      
      if (err.response?.status === 403 && err.response?.data?.upgradeRequired) {
        setUpgradeRequired(true);
        setError(t('customerDiscounts.errors.upgradeRequired'));
      } 
      else if (err.response?.status !== 404) {
        setError(t('customerDiscounts.errors.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/discounts/analytics`,
        { headers: getAuthHeaders() }
      );
      setAnalytics(response.data.analytics);
    } catch (err) {
      console.error('Fetch analytics error:', err);
    }
  };

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/wholesalers/customers`, 
        { headers: getAuthHeaders() }
      );
      setBusinesses(response.data.customers || []);
    } catch (err) {
      console.error('Fetch businesses error:', err);
    }
  };

  const fetchApprovedBusinesses = async () => {
    try {
      const response = await axios.get(`${API_URL}/wholesaler/catalog-requests`, { headers: getAuthHeaders() });
      if (response.data.approvedBusinesses) {
        setApprovedBusinesses(response.data.approvedBusinesses);
      }
    } catch (err) {
      console.error('Fetch approved businesses error:', err);
    }
  };

  const handleSaveDiscount = async (formData) => {
    if (editingDiscount) {
      await axios.put(`${API_URL}/discounts/customers/${editingDiscount._id}`, formData, { headers: getAuthHeaders() });
    } else {
      await axios.post(`${API_URL}/discounts/customers`, formData, { headers: getAuthHeaders() });
    }
    await fetchDiscounts();
    await fetchAnalytics();
  };


  const getAvailableBusinesses = () => {
    const businessMap = new Map();
    businesses.forEach(business => {
      if (business._id) {
        businessMap.set(business._id, { ...business, source: 'order', hasOrdered: true });
      }
    });
    if (approvedBusinesses && approvedBusinesses.length > 0) {
      approvedBusinesses.forEach(approved => {
        if (!businessMap.has(approved.businessId)) {
          businessMap.set(approved.businessId, {
            _id: approved.businessId,
            businessName: approved.businessName,
            businessType: approved.businessType,
            source: 'catalog',
            hasOrdered: false
          });
        }
      });
    }
    return Array.from(businessMap.values());
  };

  const availableBusinesses = getAvailableBusinesses();
  const handleDelete = async (id) => {
    setConfirmModal({
      isOpen: true,
      title: t('customerDiscounts.confirmDeleteTitle', 'Delete Discount'),
      message: t('customerDiscounts.confirmDeactivate'),
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          await axios.delete(`${API_URL}/discounts/customers/${id}`, { headers: getAuthHeaders() });
          await fetchDiscounts();
        } catch (err) {
          toast.error(t('customerDiscounts.errors.deactivateFailed'));
        }
      }
    });
  };
  
  const filteredDiscounts = discounts.filter(discount => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      discount.businessId?.businessName?.toLowerCase().includes(search) ||
      discount.reason?.toLowerCase().includes(search) ||
      discount.percentage.toString().includes(search)
    );
  });

  const {
    selectedIds, handleItemClick, selectAll, clearSelection,
    dragProps, isDragging, dragRect, dragSelectedIds,
  } = useRangeSelect(filteredDiscounts || discounts);

  const handleBulkDelete = () => {
    setConfirmModal({
      isOpen: true,
      title: t('customerDiscounts.bulkDeleteTitle', 'Delete Selected'),
      message: t('customerDiscounts.bulkDeleteMessage', { count: selectedIds.size }) || `Delete ${selectedIds.size} selected discounts?`,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        setBulkActionLoading(true);
        try {
          await axios.delete(`${API_URL}/discounts/customers/bulk`, { data: { ids: [...selectedIds] }, headers: getAuthHeaders() });
          clearSelection();
          await fetchDiscounts();
        } catch (err) {
          setError(t('customerDiscounts.errors.bulkDeleteFailed', 'Bulk delete failed'));
        } finally {
          setBulkActionLoading(false);
        }
      }
    });
  };

  const handleBulkToggle = async () => {
    setBulkActionLoading(true);
    try {
      // Determine toggle direction: if majority active, deactivate; else activate
      const selectedDiscounts = filteredDiscounts.filter(d => selectedIds.has(d._id));
      const majorityActive = selectedDiscounts.filter(d => d.active).length > selectedDiscounts.length / 2;
      const active = !majorityActive;
      await axios.put(`${API_URL}/discounts/customers/bulk-toggle`, { ids: [...selectedIds], active }, { headers: getAuthHeaders() });
      clearSelection();
      await fetchDiscounts();
    } catch (err) {
      setError(t('customerDiscounts.errors.bulkToggleFailed', 'Bulk toggle failed'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  if (upgradeRequired) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-16 lg:pt-0">
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-xl p-8 text-white text-center">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-90" />
              <h1 className="text-3xl font-bold mb-4">{t('customerDiscounts.upgrade.title')}</h1>
              <p className="text-xl mb-6 opacity-90">
                {t('customerDiscounts.upgrade.subtitle')}
              </p>
              <div className="bg-white bg-opacity-20 rounded-lg p-6 mb-6 text-left max-w-md mx-auto">
                <h3 className="font-semibold mb-3">{t('customerDiscounts.upgrade.featuresTitle')}</h3>
                <ul className="space-y-2">
                  <li>✓ {t('customerDiscounts.upgrade.feature1')}</li>
                  <li>✓ {t('customerDiscounts.upgrade.feature2')}</li>
                  <li>✓ {t('customerDiscounts.upgrade.feature3')}</li>
                  <li>✓ {t('customerDiscounts.upgrade.feature4')}</li>
                </ul>
              </div>
              <button
                onClick={() => window.location.href = '/wholesaler/subscription'}
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                {t('customerDiscounts.upgrade.button')}
              </button>
            </div>
          </main>
          <Footer />
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">{t('customerDiscounts.pageTitle')}</h1>
              <p className="text-gray-600 mt-2">{t('customerDiscounts.pageSubtitle')}</p>
            </div>
            <button
              onClick={() => { setEditingDiscount(null); setShowModal(true); }}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-md flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {t('customerDiscounts.addButton')}
            </button>
          </div>
          
          {/* Analytics Cards */}
          {analytics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">{t('customerDiscounts.stats.totalGiven')}</span>
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">€{analytics.totalDiscountGiven?.toFixed(2) || '0.00'}</p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">{t('customerDiscounts.stats.avgPerOrder')}</span>
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">€{analytics.averageDiscountPerOrder?.toFixed(2) || '0.00'}</p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">{t('customerDiscounts.stats.activeDiscounts')}</span>
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{analytics.activeCustomerDiscounts || 0}</p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">{t('customerDiscounts.stats.customerDiscounts')}</span>
                  <Users className="w-5 h-5 text-orange-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{analytics.discountsByType?.customer_discount || 0}</p>
              </div>
            </div>
          )}
          
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('customerDiscounts.filters.searchLabel')}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t('customerDiscounts.filters.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
              </div>
              
              <div className="flex items-end">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showActiveOnly}
                    onChange={(e) => setShowActiveOnly(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{t('customerDiscounts.filters.activeOnly')}</span>
                </label>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mt-4">
              {t('customerDiscounts.filters.showing', { count: filteredDiscounts.length, total: discounts.length })}
            </p>
          </div>
          
          {/* Discounts Table */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden" {...dragProps} style={{ position: 'relative' }}>
            <DragSelectOverlay isDragging={isDragging} dragRect={dragRect} />
            {loading ? (
              <div className="text-center py-12"><p className="text-gray-500">{t('common.loading')}</p></div>
            ) : filteredDiscounts.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg mb-4">
                  {searchTerm ? t('customerDiscounts.noSearchResults') : t('customerDiscounts.noDiscounts')}
                </p>
                {!searchTerm && (
                  <button
                    onClick={() => setShowModal(true)}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {t('customerDiscounts.addFirst')}
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-3 text-center w-10">
                        <input
                          type="checkbox"
                          checked={filteredDiscounts.length > 0 && selectedIds.size === filteredDiscounts.length}
                          onChange={() => selectedIds.size === filteredDiscounts.length ? clearSelection() : selectAll()}
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('customerDiscounts.table.customer')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('customerDiscounts.table.discount')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('customerDiscounts.table.target', 'Target')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('customerDiscounts.table.reason')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('customerDiscounts.table.usage')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('customerDiscounts.table.status')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('customerDiscounts.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredDiscounts.map((discount) => (
                      <tr key={discount._id} data-select-id={discount._id} className={`hover:bg-gray-50 ${selectedIds.has(discount._id) ? 'bg-indigo-50' : dragSelectedIds.has(discount._id) ? 'bg-blue-50' : ''}`}>
                        <td className="px-2 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(discount._id)}
                            readOnly onClick={(e) => { e.stopPropagation(); handleItemClick(discount._id, e); }}
                            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-semibold text-gray-900">{discount.businessId?.businessName || t('common.unknown')}</div>
                          <div className="text-xs text-gray-500">{discount.businessId?.contact?.email}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-2xl font-bold text-green-600">{discount.percentage}%</span>
                        </td>
                        <td className="px-6 py-4">
                          {(!discount.targetType || discount.targetType === 'all') ? (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">{t('customerDiscounts.labels.allProducts', 'All Products')}</span>
                          ) : (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full capitalize">
                              {discount.targetType === 'category' ? t(`products:categories.${discount.targetValue}`, discount.targetValue) : t(`products:subcategories.${discount.targetValue}`, discount.targetValue?.replace(/_/g, ' '))}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-600">{discount.reason || '-'}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{discount.timesUsed || 0} {t('customerDiscounts.table.times')}</div>
                          {discount.lastUsedAt && <div className="text-xs text-gray-500">{t('customerDiscounts.table.lastUsed')}: {new Date(discount.lastUsedAt).toLocaleDateString()}</div>}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${discount.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {discount.active ? t('common.active') : t('common.inactive')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button onClick={() => { setEditingDiscount(discount); setShowModal(true); }} className="text-blue-600 hover:text-blue-800">
                              <Edit2 className="w-5 h-5" />
                            </button>
                            <button onClick={() => handleDelete(discount._id)} className="text-red-600 hover:text-red-800">
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
      {showModal && (
        <CustomerDiscountModal
          discount={editingDiscount}
          businesses={availableBusinesses}
          onClose={() => { setShowModal(false); setEditingDiscount(null); }}
          onSave={handleSaveDiscount}
        />
      )}

      <FloatingActionBar count={selectedIds.size} onClear={clearSelection} onSelectAll={selectAll}>
        <button
          onClick={handleBulkToggle}
          disabled={bulkActionLoading}
          className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg text-sm font-medium hover:bg-amber-200 disabled:opacity-50"
        >
          <ToggleRight className="w-4 h-4 inline mr-1" />
          {t('customerDiscounts.toggleActive', 'Toggle Active')}
        </button>
        <button
          onClick={handleBulkDelete}
          disabled={bulkActionLoading}
          className="px-3 py-1.5 bg-red-100 text-red-800 rounded-lg text-sm font-medium hover:bg-red-200 disabled:opacity-50"
        >
          <Trash2 className="w-4 h-4 inline mr-1" />
          {t('customerDiscounts.deleteSelected', 'Delete Selected')}
        </button>
      </FloatingActionBar>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        icon="danger"
        confirmLabel={t('customerDiscounts.delete', 'Delete')}
        confirmColor="red"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default CustomerDiscounts;